import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingInvites, setPendingInvites] = useState([]);

  const fetchUser = useCallback(async () => {
    try {
      const res = await API.get('/auth/me');
      const u = res.data.user;
      setUser(u);
      setPendingInvites(u.pending_invites || []);
    } catch {
      localStorage.removeItem('token');
      setUser(null);
      setPendingInvites([]);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const login = async (email, password) => {
    const res = await API.post('/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    setPendingInvites(res.data.pending_invites || []);
    return res.data;
  };

  const signup = async (formData) => {
    const res = await API.post('/auth/signup', formData);
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    setPendingInvites(res.data.pending_invites || []);
    return res.data;
  };

  const loginWithToken = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setPendingInvites([]);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  // Derive permissions from team_context
  const teamContext = user?.team_context || null;
  const permissions = teamContext?.permissions || [];

  const hasPermission = (...perms) => {
    if (!teamContext || !teamContext.is_team_mode) return true; // solo mode = all perms
    const userPerms = new Set(permissions);
    return perms.every(p => userPerms.has(p));
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, signup, loginWithToken, logout,
      refreshUser, pendingInvites, setPendingInvites,
      teamContext, permissions, hasPermission,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
